import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { mcpCall, mcpWrite } from '../lib/mcp';
import { useMcpTools } from '../lib/useMcpTools';
import { useToast } from '../components/Toast/ToastProvider';
import { followupTone, stageLabel } from './Leads';
import './Leads.css';

/**
 * One lead: its details, its pipeline stage, and its history. Backlog 1082, first slice.
 *
 * THE STAGE CONTROL AND THE ACTIVITY LOG ARE THE POINT. Everything else on this page is a
 * read. A rep working a pipeline does two things — move a lead along, and write down what
 * happened — and until both exist here, `cabana-reps` cannot be retired.
 *
 * EVERY CONTROL HIDES ITSELF unless the operator's role holds the grant. `can()` reads the
 * tool list the MCP returns for that role, derived from `role_tools` — the same authority the
 * server enforces with, not a parallel guess. So on a role without the rep-CRM grants this
 * page degrades to a read-only record rather than showing buttons that 403.
 *
 * STAGE CHANGES AUTO-LOG. `updateLeadStage` writes a `stage_change` activity and bumps
 * `last_activity_at` in the same transaction — so the history below is complete without this
 * page logging anything itself, and it must NOT log a duplicate.
 */

interface Lead {
  id: number;
  public_id: string;
  company_name: string;
  contact_first_name: string | null;
  contact_last_name: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  primary_zip: string | null;
  territory: string | null;
  stage: string;
  interest_level: string | null;
  source: string | null;
  lost_reason: string | null;
  next_followup_at: string | null;
  last_activity_at: string | null;
  assigned_rep_id: number | null;
  converted_dealer_id: number | null;
  status: string;
  created_at: string;
}

interface Activity {
  id: number;
  lead_id: number;
  rep_id: number | null;
  activity_type: string;
  body: string | null;
  metadata: Record<string, unknown> | null;
  occurred_at: string;
}

interface Appointment {
  id: number;
  title: string | null;
  appointment_type: string;
  scheduled_at: string;
  duration_minutes: number | null;
  location: string | null;
  status: string;
  notes: string | null;
}

const STAGES = ['new', 'contacted', 'qualified', 'demo_scheduled', 'proposal', 'won', 'lost'] as const;
const ACTIVITY_TYPES = ['call', 'note', 'email', 'sms', 'meeting'] as const;

function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function LeadDetail() {
  const { id } = useParams();
  const leadId = Number(id);
  const { can } = useMcpTools();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  const [stage, setStage] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [activityType, setActivityType] = useState<string>('call');
  const [activityBody, setActivityBody] = useState('');
  const [busy, setBusy] = useState(false);

  const lead = useQuery({
    queryKey: ['mcp', 'getLead', id],
    queryFn: () => mcpCall<Lead | null>('getLead', { id: leadId }),
    enabled: can('getLead') && Number.isFinite(leadId),
  });

  const activities = useQuery({
    queryKey: ['mcp', 'listLeadActivities', id],
    queryFn: () => mcpCall<{ activities: Activity[] }>('listLeadActivities', { leadId, limit: 50 }),
    enabled: can('listLeadActivities') && Number.isFinite(leadId),
  });

  const appointments = useQuery({
    queryKey: ['mcp', 'listLeadAppointments', id],
    queryFn: () => mcpCall<{ appointments: Appointment[] }>('listLeadAppointments', { leadId, limit: 20 }),
    enabled: can('listLeadAppointments') && Number.isFinite(leadId),
  });

  const l = lead.data ?? null;

  /** Everything this page can change lives on the lead or its history, so refresh all three. */
  async function refreshAll() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['mcp', 'getLead', id] }),
      queryClient.invalidateQueries({ queryKey: ['mcp', 'listLeadActivities', id] }),
      queryClient.invalidateQueries({ queryKey: ['mcp', 'listLeads'] }),
    ]);
  }

  async function moveStage() {
    if (busy || !stage || stage === l?.stage) return;
    setBusy(true);
    try {
      // lostReason only when moving to lost — the tool takes it optionally and sending it
      // otherwise records a reason against a stage that has none.
      const args: Record<string, unknown> = { id: leadId, stage };
      if (stage === 'lost' && lostReason.trim()) args.lostReason = lostReason.trim();
      await mcpWrite('updateLeadStage', args);
      notify({ variant: 'success', message: `Moved to ${stageLabel(stage)}.` });
      setStage('');
      setLostReason('');
      await refreshAll();
    } catch (err) {
      notify({ variant: 'error', message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function addActivity() {
    if (busy || !activityBody.trim()) return;
    setBusy(true);
    try {
      await mcpWrite('logActivity', { leadId, activityType, body: activityBody.trim() });
      notify({ variant: 'success', message: 'Logged.' });
      setActivityBody('');
      await refreshAll();
    } catch (err) {
      notify({ variant: 'error', message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (lead.isLoading) return <section className="records"><p>Loading…</p></section>;

  return (
    <section className="records">
      <header className="records__head">
        <div className="records__head-text">
          <Link to="/leads" className="rep-detail__back">← Leads</Link>
          <h1 className="records__title">{l?.company_name ?? `Lead #${id}`}</h1>
          {l && (
            <p className="records__intro">
              <span className={`leads__stage leads__stage--${l.stage}`}>{stageLabel(l.stage)}</span>
              {l.interest_level && (
                <span className={`leads__interest leads__interest--${l.interest_level}`}>{l.interest_level}</span>
              )}
              {l.status !== 'active' && <span className="leads__stage leads__stage--lost">{l.status}</span>}
            </p>
          )}
        </div>
      </header>

      {!l && !lead.isLoading && <div className="records__panel"><p>No lead has that id.</p></div>}

      {l && (
        <>
          <div className="records__panel">
            <table className="leads__facts">
              <tbody>
                <tr><th>Contact</th><td>{[l.contact_first_name, l.contact_last_name].filter(Boolean).join(' ') || '—'}</td></tr>
                <tr><th>Email</th><td>{l.email ? <a href={`mailto:${l.email}`}>{l.email}</a> : '—'}</td></tr>
                <tr><th>Phone</th><td>{l.phone ? <a href={`tel:${l.phone}`}>{l.phone}</a> : '—'}</td></tr>
                <tr><th>Website</th><td>{l.website || '—'}</td></tr>
                <tr><th>Territory</th><td>{l.territory || '—'}</td></tr>
                <tr><th>ZIP</th><td>{l.primary_zip || '—'}</td></tr>
                <tr><th>Source</th><td>{l.source ? stageLabel(l.source) : '—'}</td></tr>
                <tr><th>Assigned rep</th><td>{l.assigned_rep_id ? <Link to={`/reps/${l.assigned_rep_id}`}>Rep #{l.assigned_rep_id}</Link> : <em>unassigned</em>}</td></tr>
                <tr>
                  <th>Next follow-up</th>
                  <td className={`leads__followup leads__followup--${followupTone(l.next_followup_at)}`}>
                    {when(l.next_followup_at)}
                  </td>
                </tr>
                <tr><th>Last activity</th><td>{when(l.last_activity_at)}</td></tr>
                {l.lost_reason && <tr><th>Lost reason</th><td>{l.lost_reason}</td></tr>}
                {l.converted_dealer_id && (
                  <tr><th>Became dealer</th><td><Link to={`/dealers/${l.converted_dealer_id}`}>Dealer #{l.converted_dealer_id}</Link></td></tr>
                )}
                <tr><th>Created</th><td>{when(l.created_at)}</td></tr>
              </tbody>
            </table>
          </div>

          {can('updateLeadStage') && (
            <div className="records__panel">
              <h2 className="leads__h2">Move stage</h2>
              <div className="leads__stagebar">
                <select
                  className="settings__input"
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  disabled={busy}
                  aria-label="New stage"
                >
                  <option value="">Choose a stage…</option>
                  {STAGES.filter((s) => s !== l.stage).map((s) => (
                    <option key={s} value={s}>{stageLabel(s)}</option>
                  ))}
                </select>
                {stage === 'lost' && (
                  <input
                    className="settings__input"
                    placeholder="Why was it lost? (optional)"
                    value={lostReason}
                    onChange={(e) => setLostReason(e.target.value)}
                    maxLength={500}
                    disabled={busy}
                  />
                )}
                <button type="button" className="settings__save" onClick={moveStage} disabled={busy || !stage}>
                  {busy ? 'Saving…' : 'Move'}
                </button>
              </div>
              <p className="settings__desc">
                A stage change logs itself in the history below and bumps the last-activity date.
                You do not need to also write a note for it.
              </p>
            </div>
          )}

          {can('logActivity') && (
            <div className="records__panel">
              <h2 className="leads__h2">Log an activity</h2>
              <div className="leads__stagebar">
                <select
                  className="settings__input"
                  value={activityType}
                  onChange={(e) => setActivityType(e.target.value)}
                  disabled={busy}
                  aria-label="Activity type"
                >
                  {ACTIVITY_TYPES.map((t) => (
                    <option key={t} value={t}>{stageLabel(t)}</option>
                  ))}
                </select>
                <input
                  className="settings__input leads__grow"
                  placeholder="What happened?"
                  value={activityBody}
                  onChange={(e) => setActivityBody(e.target.value)}
                  maxLength={5000}
                  disabled={busy}
                />
                <button type="button" className="settings__save" onClick={addActivity} disabled={busy || !activityBody.trim()}>
                  {busy ? 'Saving…' : 'Log'}
                </button>
              </div>
            </div>
          )}

          <div className="records__panel">
            <h2 className="leads__h2">History</h2>
            {activities.isLoading && <p>Loading…</p>}
            {!activities.isLoading && (activities.data?.activities ?? []).length === 0 && (
              <p className="settings__desc">Nothing logged yet.</p>
            )}
            <ul className="leads__timeline">
              {(activities.data?.activities ?? []).map((a) => (
                <li key={a.id} className="leads__event">
                  <span className={`leads__kind leads__kind--${a.activity_type}`}>{a.activity_type.replace(/_/g, ' ')}</span>
                  <span className="leads__when">{when(a.occurred_at)}</span>
                  {a.body && <p className="leads__body">{a.body}</p>}
                </li>
              ))}
            </ul>
          </div>

          <div className="records__panel">
            <h2 className="leads__h2">Appointments</h2>
            {appointments.isLoading && <p>Loading…</p>}
            {!appointments.isLoading && (appointments.data?.appointments ?? []).length === 0 && (
              <p className="settings__desc">
                None scheduled. Scheduling from this console is not built yet — see the note in
                the register about which rep-portal screens have moved.
              </p>
            )}
            <ul className="leads__timeline">
              {(appointments.data?.appointments ?? []).map((ap) => (
                <li key={ap.id} className="leads__event">
                  <span className={`leads__kind leads__kind--${ap.appointment_type}`}>{ap.appointment_type.replace(/_/g, ' ')}</span>
                  <span className="leads__when">{when(ap.scheduled_at)}</span>
                  <span className={`leads__stage leads__stage--${ap.status === 'completed' ? 'won' : ap.status === 'cancelled' || ap.status === 'no_show' ? 'lost' : 'new'}`}>
                    {ap.status.replace(/_/g, ' ')}
                  </span>
                  {ap.title && <p className="leads__body">{ap.title}{ap.location ? ` — ${ap.location}` : ''}</p>}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </section>
  );
}
